import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, ShieldCheck, CheckCircle2, ArrowRight, ArrowLeft, Smartphone, MessageCircle, QrCode } from "lucide-react";
import { onOpenBooking, loadRazorpay } from "../../lib/booking";
import { SERVICE_OPTIONS, BRAND } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const EMPTY = { name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], message: "" };

export default function BookingDialog() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("details");
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [channel, setChannel] = useState("sms");
  const [otpSent, setOtpSent] = useState(false);
  const [devCode, setDevCode] = useState("");
  const [code, setCode] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [order, setOrder] = useState(null);
  const [txnRef, setTxnRef] = useState("");
  const [loading, setLoading] = useState(false);

  const price = config?.service_prices?.[form.service] ?? 0;

  const reset = useCallback(() => {
    setStep("details"); setForm(EMPTY); setChannel("sms"); setOtpSent(false);
    setDevCode(""); setCode(""); setBookingId(""); setOrder(null); setTxnRef(""); setLoading(false);
  }, []);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
    const off = onOpenBooking((service) => {
      reset();
      if (service) setForm((f) => ({ ...f, service }));
      setOpen(true);
    });
    return off;
  }, [reset]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitDetails = () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill your name, email and phone.");
      return;
    }
    setStep("otp");
    setOtpSent(false);
    setDevCode("");
    setCode("");
  };

  const sendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/otp/send`, { phone: form.phone, channel });
      setOtpSent(true);
      if (data.dev_mode && data.dev_code) {
        setDevCode(data.dev_code);
        toast.info("Test mode: OTP shown on screen (Twilio not configured yet).");
      } else {
        toast.success(`OTP sent via ${channel === "whatsapp" ? "WhatsApp" : "SMS"}.`);
      }
    } catch {
      toast.error("Could not send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyAndBook = async () => {
    if (code.length < 4) { toast.error("Enter the OTP code."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/otp/verify`, { phone: form.phone, code });
      if (!data.valid) { toast.error("Invalid or expired OTP."); setLoading(false); return; }
      const b = await axios.post(`${API}/bookings`, form);
      setBookingId(b.data.id);
      const o = await axios.post(`${API}/payments/order`, { booking_id: b.data.id });
      setOrder(o.data);
      setStep("payment");
    } catch {
      toast.error("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const payWithRazorpay = async () => {
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Payment SDK failed to load."); return; }
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "ONE VISION",
      description: form.service,
      order_id: order.order_id,
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#16A34A" },
      handler: async (res) => {
        try {
          await axios.post(`${API}/payments/verify`, {
            booking_id: bookingId,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          setStep("done");
        } catch {
          toast.error("Payment could not be verified. Contact us with your reference.");
        }
      },
    });
    rzp.open();
  };

  const submitManual = async () => {
    if (txnRef.trim().length < 4) { toast.error("Enter your UPI transaction / reference ID."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/payments/manual`, { booking_id: bookingId, upi_txn_ref: txnRef.trim() });
      setStep("done");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-lg p-0 gap-0 overflow-hidden bg-ov-bone border-ov-navy/10"
      >
        {/* Header */}
        <DialogHeader className="bg-ov-navy px-7 py-6 text-left space-y-1">
          <div className="flex items-center gap-2 text-ov-greenBright text-xs uppercase tracking-[0.2em] font-semibold font-body">
            <ShieldCheck size={15} /> Book & Verify
          </div>
          <DialogTitle className="font-display text-2xl text-white">
            {step === "done" ? "You're all set" : "Start your property verification"}
          </DialogTitle>
          <DialogDescription className="font-body text-white/60 text-sm">
            {step === "details" && "Tell us about the service you need."}
            {step === "otp" && "Verify your phone number to continue."}
            {step === "payment" && "Secure your booking with payment."}
            {step === "done" && "We've received your booking."}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        {step !== "done" && (
          <div className="flex items-center gap-2 px-7 pt-5">
            {["details", "otp", "payment"].map((s, i) => {
              const active = ["details", "otp", "payment"].indexOf(step) >= i;
              return (
                <div key={s} className="flex-1 h-1 rounded-full overflow-hidden bg-ov-navy/10">
                  <div className={`h-full transition-all duration-500 ${active ? "w-full bg-ov-green" : "w-0"}`} />
                </div>
              );
            })}
          </div>
        )}

        <div className="px-7 py-6 font-body">
          <AnimatePresence mode="wait">
            {/* STEP 1: DETAILS */}
            {step === "details" && (
              <motion.div key="details" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} className="space-y-4">
                <Field label="Full Name">
                  <input data-testid="booking-name" className="ov-input" value={form.name} onChange={update("name")} placeholder="Your name" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Email">
                    <input data-testid="booking-email" type="email" className="ov-input" value={form.email} onChange={update("email")} placeholder="you@email.com" />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="booking-phone" className="ov-input" value={form.phone} onChange={update("phone")} placeholder="10-digit mobile" />
                  </Field>
                </div>
                <Field label="Service">
                  <select data-testid="booking-service" className="ov-input bg-transparent" value={form.service} onChange={update("service")}>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{`${s} — ${inr(config?.service_prices?.[s])}`}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Message (optional)">
                  <textarea data-testid="booking-message" rows={2} className="ov-input resize-none" value={form.message} onChange={update("message")} placeholder="Property details" />
                </Field>

                <div className="flex items-center justify-between pt-2">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ov-charcoal/50">Fee</div>
                    <div className="font-display text-2xl text-ov-navy" data-testid="booking-price">{inr(price)}</div>
                  </div>
                  <Button data-testid="booking-details-continue" onClick={submitDetails} className="rounded-full bg-ov-navy hover:bg-ov-green text-white px-6">
                    Continue <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* STEP 2: OTP */}
            {step === "otp" && (
              <motion.div key="otp" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-ov-charcoal/50 mb-2 font-semibold">Send code via</div>
                  <div className="grid grid-cols-2 gap-3">
                    {[["sms", "SMS", Smartphone], ["whatsapp", "WhatsApp", MessageCircle]].map(([val, label, Icon]) => (
                      <button
                        key={val}
                        data-testid={`booking-channel-${val}`}
                        onClick={() => setChannel(val)}
                        className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm transition-colors ${channel === val ? "border-ov-green bg-ov-greenPale text-ov-navy" : "border-ov-navy/15 text-ov-charcoal/70 hover:border-ov-green"}`}
                      >
                        <Icon size={16} className={channel === val ? "text-ov-green" : ""} /> {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="text-sm text-ov-charcoal/70">
                  Sending to <span className="font-semibold text-ov-navy">{form.phone}</span>
                </div>

                {!otpSent ? (
                  <Button data-testid="booking-send-otp" disabled={loading} onClick={sendOtp} className="w-full rounded-full bg-ov-navy hover:bg-ov-green text-white">
                    {loading ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Send OTP
                  </Button>
                ) : (
                  <div className="space-y-4">
                    {devCode && (
                      <div data-testid="booking-dev-code" className="rounded-xl bg-ov-greenPale border border-ov-green/30 px-4 py-3 text-sm text-ov-navy">
                        <span className="font-semibold">Test mode code:</span> {devCode}
                        <span className="block text-xs text-ov-charcoal/60 mt-0.5">Live SMS/WhatsApp starts once Twilio keys are added.</span>
                      </div>
                    )}
                    <Field label="Enter OTP">
                      <input data-testid="booking-otp-input" className="ov-input tracking-[0.5em] text-lg" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="______" maxLength={6} />
                    </Field>
                    <button data-testid="booking-resend" onClick={sendOtp} className="text-xs text-ov-green underline underline-offset-4">Resend code</button>
                    <Button data-testid="booking-verify" disabled={loading} onClick={verifyAndBook} className="w-full rounded-full bg-ov-navy hover:bg-ov-green text-white">
                      {loading ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Verify & Continue
                    </Button>
                  </div>
                )}

                <button onClick={() => setStep("details")} className="flex items-center gap-1 text-xs text-ov-charcoal/50 hover:text-ov-navy">
                  <ArrowLeft size={13} /> Back
                </button>
              </motion.div>
            )}

            {/* STEP 3: PAYMENT */}
            {step === "payment" && order && (
              <motion.div key="payment" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} className="space-y-5">
                <div className="flex items-center justify-between rounded-xl bg-white border border-ov-navy/10 px-4 py-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-ov-charcoal/50">{form.service}</div>
                    <div className="font-display text-2xl text-ov-navy">{inr(order.amount_inr)}</div>
                  </div>
                  <ShieldCheck className="text-ov-green" size={26} />
                </div>

                {order.mode === "razorpay" ? (
                  <Button data-testid="booking-pay-razorpay" onClick={payWithRazorpay} className="w-full rounded-full bg-ov-green hover:bg-ov-navy text-white">
                    Pay {inr(order.amount_inr)} securely
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-ov-charcoal/70">
                      <QrCode size={16} className="text-ov-green" /> Scan & pay to <span className="font-semibold text-ov-navy">{order.payee_name}</span>
                    </div>
                    {order.upi_qr_url && (
                      <div className="flex justify-center">
                        <img data-testid="booking-upi-qr" src={order.upi_qr_url} alt="UPI QR" className="w-48 h-48 object-contain rounded-xl border border-ov-navy/10 bg-white p-2" />
                      </div>
                    )}
                    <Field label="UPI Transaction / Reference ID">
                      <input data-testid="booking-txn-ref" className="ov-input" value={txnRef} onChange={(e) => setTxnRef(e.target.value)} placeholder="e.g. T2401XXXXXXXX" />
                    </Field>
                    <Button data-testid="booking-submit-manual" disabled={loading} onClick={submitManual} className="w-full rounded-full bg-ov-navy hover:bg-ov-green text-white">
                      {loading ? <Loader2 size={16} className="animate-spin mr-1" /> : null} I&apos;ve paid — Submit
                    </Button>
                  </div>
                )}
              </motion.div>
            )}

            {/* DONE */}
            {step === "done" && (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
                <CheckCircle2 size={54} className="text-ov-green mx-auto mb-5" />
                <h3 className="font-display text-2xl text-ov-navy">Booking confirmed</h3>
                <p className="text-ov-charcoal/70 mt-2 text-sm max-w-sm mx-auto">
                  Thank you, {form.name}. Our expert panel will contact you on {form.phone} shortly to begin the verification.
                </p>
                <div className="mt-4 text-xs text-ov-charcoal/50">Reference: {bookingId.slice(0, 8).toUpperCase()}</div>
                <Button data-testid="booking-close" onClick={() => setOpen(false)} className="mt-6 rounded-full bg-ov-navy hover:bg-ov-green text-white px-8">
                  Done
                </Button>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="block mt-4 text-xs text-ov-green underline underline-offset-4">
                  Chat with us on WhatsApp
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-ov-charcoal/50 font-semibold">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
